import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { getCurrentPhase, getTrackingMode } from '../../utils/cycleCalculator'

const CoachTab = ({ user, userProfile }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'coach',
      content: "Hi! I'm your Cycle Coach. I can help you adapt any guidance to fit your exact situation. What doesn't work for you today?",
      timestamp: new Date()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  const currentPhase = getCurrentPhase()
  const trackingMode = userProfile?.tracking_mode || getTrackingMode() || 'cycle'

  const quickPrompts = [
    "This doesn't fit my schedule today",
    "I need alternatives for working from home",
    "I'm feeling different than expected",
    "How do I adapt this for my energy level?",
    "I have kids at home today",
    "I'm traveling this week"
  ]

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setIsTyping(true)

    // Simulate coach response
    setTimeout(() => {
      const coachResponse = generateCoachResponse(inputMessage, currentPhase, trackingMode)
      const coachMessage = {
        id: messages.length + 2,
        type: 'coach',
        content: coachResponse,
        timestamp: new Date()
      }
      
      setMessages(prev => [...prev, coachMessage])
      setIsTyping(false)
    }, 1500)
  }

  const generateCoachResponse = (userInput, phase, mode) => {
    const input = userInput.toLowerCase()
    
    // Context about current phase
    const phaseContext = phase ? {
      name: phase.name,
      description: phase.description,
      cycleDay: phase.cycleDay,
      lunarDay: phase.lunarDay
    } : null

    if (input.includes('schedule') || input.includes('time') || input.includes('busy')) {
      return `I understand you're pressed for time! Since you're in ${phaseContext?.name || 'your current phase'}, here are 3 quick alternatives:

1. **5-minute version**: Choose just ONE action that feels most aligned with your energy right now
2. **Micro-moments**: Break the guidance into 2-3 small actions you can do between other tasks  
3. **Energy-first approach**: Instead of following the exact actions, just ask yourself "What does my ${mode === 'cycle' ? 'cycle' : 'lunar'} energy need right now?" and do that

Which feels most doable for you today?`
    }

    if (input.includes('home') || input.includes('remote') || input.includes('kids')) {
      return `Working from home changes everything! For ${phaseContext?.name || 'your phase'}, try these adaptations:

1. **Home environment**: Create a small ritual space - even just lighting a candle can shift your energy
2. **Micro-breaks**: Use transition moments (between calls, while coffee brews) for mini-actions
3. **Family integration**: Include others in gentle ways - kids can help with organizing, partners can support your rest time

The key is adapting the ENERGY of the guidance to your actual environment. What part of your home feels most supportive right now?`
    }

    if (input.includes('energy') || input.includes('tired') || input.includes('different')) {
      return `Your energy is the best guide! If the suggested actions don't match how you're feeling, here's how to adapt:

**If you have LESS energy than expected:**
- Choose the gentlest action from the list
- Do a "thinking" version instead of "doing" (visualize instead of act)
- Focus on planning rather than executing

**If you have MORE energy:**
- Combine 2-3 actions into a power session  
- Add your own intuitive actions that feel aligned
- Use the extra energy to prepare for your next phase

Trust your body's wisdom - it knows better than any app what you need today. What's your energy telling you?`
    }

    if (input.includes('travel') || input.includes('away')) {
      return `Traveling during ${phaseContext?.name || 'this phase'} requires flexibility! Here are travel-friendly adaptations:

1. **Airplane/car time**: Perfect for reflection and planning activities
2. **Hotel room rituals**: Use whatever space you have for gentle movement or meditation
3. **Exploration energy**: Align sightseeing and activities with your phase energy (${phaseContext?.name === 'Radiant Power' ? 'perfect for social activities!' : phaseContext?.name === 'Deep Renewal' ? 'seek quiet, restorative experiences' : 'adapt based on your phase'})

The most important thing while traveling is maintaining some connection to your rhythm, even if it looks different. What kind of trip are you on?`
    }

    // Default personalized response
    return `I hear you! Let me help you adapt this guidance to fit your real life. 

Since you're in ${phaseContext?.name || 'your current phase'} ${phaseContext?.cycleDay ? `(day ${phaseContext.cycleDay})` : phaseContext?.lunarDay ? `(lunar day ${phaseContext.lunarDay})` : ''}, the core energy is about ${phaseContext?.description?.split('.')[0].toLowerCase() || 'working with your natural rhythm'}.

Instead of following the exact actions, try asking yourself:
- What would honor this energy in MY current situation?
- What's the smallest step I could take that feels aligned?
- How can I adapt this to my actual life today?

Can you tell me more about your specific situation so I can give you better alternatives?`
  }

  const handleQuickPrompt = (prompt) => {
    setInputMessage(prompt)
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="flex flex-col h-96">
      {/* Chat Header */}
      <div className="bg-gold bg-opacity-10 p-4 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
            <span className="text-white font-semibold">CC</span>
          </div>
          <div>
            <h3 className="font-semibold text-black">Cycle Coach</h3>
            <p className="text-sm text-gray-600">
              {currentPhase ? `Guiding you through ${currentPhase.name}` : 'Your personal cycle guide'}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 p-4 space-y-4 overflow-y-auto bg-gray-50">
        {messages.map((message) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs p-3 rounded-lg ${
                message.type === 'user'
                  ? 'bg-gold text-white'
                  : 'bg-white text-gray-800 border border-gray-200'
              }`}
            >
              <p className="text-sm leading-relaxed whitespace-pre-line">
                {message.content}
              </p>
              <p className={`text-xs mt-2 ${
                message.type === 'user' ? 'text-gold-200' : 'text-gray-500'
              }`}>
                {formatTime(message.timestamp)}
              </p>
            </div>
          </motion.div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 p-3 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Prompts */}
      {messages.length === 1 && (
        <div className="p-4 bg-white border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Try asking:</p>
          <div className="space-y-2">
            {quickPrompts.slice(0, 3).map((prompt, index) => (
              <button
                key={index}
                onClick={() => handleQuickPrompt(prompt)}
                className="w-full text-left text-sm p-2 bg-gray-50 hover:bg-gray-100 rounded border transition-colors"
              >
                "{prompt}"
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-gray-200 rounded-b-lg">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Tell me what doesn't fit your situation..."
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold text-sm"
            disabled={isTyping}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            className="gold-bg text-white px-4 py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            →
          </button>
        </div>
      </div>

      {/* Coach Info */}
      <div className="mt-4 bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-black mb-2">💬 How Your Coach Helps</h4>
        <div className="space-y-1 text-sm text-gray-600">
          <div>• Adapts guidance to your real-life situations</div>
          <div>• Provides alternatives when standard advice doesn't fit</div>
          <div>• Helps you work WITH your energy, not against it</div>
          <div>• Available 24/7 for personalized support</div>
        </div>
      </div>
    </div>
  )
}

export default CoachTab